<div class="inner-content">
    <app-old-system-iframe
        [hidden]="!headlineService.moduleStates['inspector']"
        (show)="headlineService.moduleStates['inspector'] = $event"
        src="administration/Inspector"
        key="check_hippo"></app-old-system-iframe>

    @if (!headlineService.moduleStates['inspector']) {
        @if (loading) {
            <mat-progress-bar
                class="ins-loading"
                mode="indeterminate"></mat-progress-bar>
        } @else if (!hasPageAccess) {
            <div class="h-full">
                <app-no-page-access></app-no-page-access>
            </div>
        } @else if (data) {
            <div class="ins-page h-full overflow-auto">
                <div class="ins-shell">
                    <header class="ins-bar">
                        <span class="ins-bar__icon">
                            <lucide-icon
                                [img]="ScanSearchIcon"
                                [size]="20"></lucide-icon>
                        </span>
                        <h2 class="ins-bar__title">
                            {{ 'codext.insTitle' | transloco }}
                        </h2>
                        <div class="ins-check">
                            <span class="ins-check__label">
                                {{
                                    (checkedDay
                                        ? 'codext.insCheckedDay'
                                        : 'codext.insLastCheck'
                                    ) | transloco
                                }}
                            </span>
                            <span class="ins-check__value">
                                @if (checkedDay) {
                                    {{ checkedDay | date: dateFormat }}
                                } @else if (checkedAt) {
                                    {{ checkedAt | date: dateFormat }}
                                    <span class="ins-check__time">
                                        {{ checkedAt | date: 'HH:mm:ss' }}
                                    </span>
                                } @else {
                                    {{ data.date }}
                                }
                            </span>
                            <span class="ins-check__nav">
                                <input
                                    class="ins-check__input"
                                    [ngModel]="date"
                                    (ngModelChange)="selectDay($event)"
                                    [max]="today"
                                    [matDatepicker]="dayPicker" />
                                <app-date-picker-toggle
                                    compact
                                    [for]="dayPicker"></app-date-picker-toggle>
                                <mat-datepicker
                                    #dayPicker
                                    (opened)="refreshToday()"></mat-datepicker>
                                @if (date) {
                                    <button
                                        type="button"
                                        class="ins-today"
                                        (click)="resetTimestamp()">
                                        {{ 'codext.insToday' | transloco }}
                                    </button>
                                }
                            </span>
                        </div>
                    </header>

                    <div class="ins-grid">
                        <section class="ins-card">
                            <h3 class="ins-band">
                                {{ 'codext.insLogFiles' | transloco }}
                            </h3>
                            @for (row of logRows; track row.folder) {
                                @let status = row.status(data);
                                <div class="ins-row">
                                    <ng-container
                                        *ngTemplateOutlet="
                                            dot;
                                            context: {
                                                tone: logTone(status),
                                                label: statusLabel(status),
                                            }
                                        "></ng-container>
                                    <span class="ins-row__label">{{
                                        row.label
                                    }}</span>
                                    <button
                                        type="button"
                                        class="ins-action"
                                        (click)="openLogFileWindow(row.folder)">
                                        {{ 'codext.insOpenLog' | transloco }}
                                        <lucide-icon
                                            [img]="ExternalLinkIcon"
                                            [size]="14"></lucide-icon>
                                    </button>
                                </div>
                            }
                        </section>

                        <section class="ins-card">
                            <h3 class="ins-band">
                                {{ 'codext.insDatabases' | transloco }}
                            </h3>
                            @for (row of databaseRows; track row.label) {
                                @let status = row.status(data);
                                <div class="ins-row">
                                    <ng-container
                                        *ngTemplateOutlet="
                                            dot;
                                            context: {
                                                tone: logTone(status),
                                                label: statusLabel(status),
                                            }
                                        "></ng-container>
                                    <span class="ins-row__label">{{
                                        row.label
                                    }}</span>
                                </div>
                            }
                        </section>

                        <section class="ins-card">
                            <h3 class="ins-band">
                                {{ 'codext.insDatabaseIndexes' | transloco }}
                            </h3>
                            @for (row of indexRows; track row.db) {
                                @let check = indexChecks[row.db];
                                <div class="ins-row">
                                    @if (check.state === 'running') {
                                        <span class="ins-dot">
                                            <lucide-icon
                                                class="ins-spin text-[var(--hippo-navy-soft)]"
                                                [img]="LoaderIcon"
                                                [size]="14"></lucide-icon>
                                        </span>
                                    } @else {
                                        <ng-container
                                            *ngTemplateOutlet="
                                                dot;
                                                context: {
                                                    tone: indexTone(row.db),
                                                    label:
                                                        check.state === 'idle'
                                                            ? ('codext.insStatusUnchecked'
                                                              | transloco)
                                                            : check.messageKey
                                                              ? (check.messageKey
                                                                | transloco)
                                                              : check.message,
                                                }
                                            "></ng-container>
                                    }
                                    <span class="ins-row__label">{{
                                        row.label
                                    }}</span>
                                    <span
                                        class="ins-row__note"
                                        [class.is-error]="
                                            check.state === 'error'
                                        "
                                        aria-live="polite"
                                        [textContent]="
                                            check.messageKey
                                                ? (check.messageKey | transloco)
                                                : (check.message ?? '')
                                        "></span>
                                    <button
                                        type="button"
                                        class="ins-action"
                                        [attr.aria-disabled]="
                                            check.state === 'running'
                                        "
                                        (click)="checkIndexes(row.db)">
                                        {{
                                            (check.state === 'running'
                                                ? 'codext.insCheckingIndexes'
                                                : 'codext.insCheckIndexes'
                                            ) | transloco
                                        }}
                                    </button>
                                </div>
                            }
                        </section>

                        <section class="ins-card">
                            <h3 class="ins-band">
                                {{ 'codext.insJobs' | transloco }}
                            </h3>
                            <div class="ins-row">
                                <ng-container
                                    *ngTemplateOutlet="
                                        dot;
                                        context: {
                                            tone: invoiceTone(
                                                data.jobs.invoice_check
                                                    .file_status
                                            ),
                                            label: invoiceLabel(
                                                data.jobs.invoice_check
                                                    .file_status
                                            ),
                                        }
                                    "></ng-container>
                                <span class="ins-row__label"
                                    >invoice check</span
                                >
                                <button
                                    type="button"
                                    class="ins-action"
                                    (click)="requestInvoiceInformation()">
                                    {{ 'codext.insStartAudit' | transloco }}
                                    <lucide-icon
                                        [img]="PlayIcon"
                                        [size]="14"></lucide-icon>
                                </button>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        }
    }
</div>

<ng-template
    #dot
    let-tone="tone"
    let-label="label">
    <span
        class="ins-dot"
        role="img"
        [attr.aria-label]="label"
        [title]="label">
        <lucide-icon
            class="filled-bullet"
            [ngClass]="toneClass(tone)"
            [img]="CircleIcon"
            [size]="12"></lucide-icon>
    </span>
</ng-template>
